import _ from 'lodash'

import {
  caseSolutionCancellationJurisdictions,
  caseSolutionItemAdditionalFields,
  CaseSolutionItemType,
  caseSolutionOppositionJurisdictions,
  subscriptionWithEnforcementFreeItemTypes,
} from '@/utils/case-solution/constants'
import {Jurisdiction} from '@/utils/countries/constants'
import {
  CaseCounterpartyFragment,
  CaseSolutionForUserFragment,
  Order_By,
  ServiceRequestFragment,
} from '@/utils/graphqlSdk'
import {prices} from '@/utils/pricing/prices'
import {Service, ServiceRequestInternalType} from '@/utils/service-request/types'

import {BrandSubscriptionTier} from '../brand-subscription/constants'
import {Currency} from '../currency'
import {caseCounterpartyOrm} from '../orm-tools/caseCounterparty'
import {CaseSolutionItem} from '../validations/common/case'

export const isCaseSolutionOrder = (serviceRequest: ServiceRequestFragment) =>
  serviceRequest.internal_label === ServiceRequestInternalType.caseSolution &&
  serviceRequest.service_name === Service.caseSolution

export const getCaseSolutionTypeJurisdictions: (type: CaseSolutionItemType) => Jurisdiction[] = (
  type,
) => {
  switch (type) {
    case CaseSolutionItemType.opposition:
      return caseSolutionOppositionJurisdictions
    case CaseSolutionItemType.cancellation:
      return caseSolutionCancellationJurisdictions
    default:
      return []
  }
}

const typeToKeyMapping: Record<CaseSolutionItemType, string> = {
  [CaseSolutionItemType.takedown]: 'takedown',
  [CaseSolutionItemType.opposition]: 'opposition',
  [CaseSolutionItemType.warningLetter]: 'warning-letter',
  [CaseSolutionItemType.ceaseDesistLetter]: 'c&d-letter',
  [CaseSolutionItemType.negotiations]: 'negotiations',
  [CaseSolutionItemType.domainDispute]: 'domain-dispute',
  [CaseSolutionItemType.cancellation]: 'cancellation',
  [CaseSolutionItemType.legalConsultation]: 'legal-consultation',
  [CaseSolutionItemType.usLetterOfProtest]: 'US-letter-protest',
}

// SKU shared by an item's enforcement service + filing catalog products
// (jurisdiction-type-platform-option); also the key into enforcement*[key].
export const getCaseSolutionItemKey = (
  type: CaseSolutionItemType,
  additionalInfo: {platform?: string; jurisdiction?: Jurisdiction; option?: string},
): string =>
  ['jurisdiction', 'type', 'platform', 'option']
    .filter(
      (field) =>
        field === 'type' ||
        (caseSolutionItemAdditionalFields[field].includes(type) &&
          !(field === 'platform' && type === CaseSolutionItemType.takedown)),
    )
    .map((field) => (field === 'type' ? typeToKeyMapping[type] : (additionalInfo as any)[field]))
    .filter((value) => !_.isEmpty(value))
    .map((value) => value.replaceAll('_', '-'))
    .join('-')

export const getSolutionServiceFees: (
  type: CaseSolutionItemType,
  currency: Currency,
  additionalInfo: {
    platform?: string
    jurisdiction?: Jurisdiction
    option?: string
  },
) => {serviceFee: number | null; governmentFee: number | null} = (
  type,
  currency,
  additionalInfo,
) => {
  const key = getCaseSolutionItemKey(type, additionalInfo)

  return {
    serviceFee: prices.enforcementServiceFees[key]?.[currency] ?? null,
    governmentFee: prices.enforcementFilingFees[key]?.[currency] ?? null,
  }
}

export const removeEmptySolutionItems = (items: Array<Record<string, any>>) =>
  items.map((item) => _.pickBy(item, (value) => value !== null && value !== ''))

export const findMatchingCounterparties = async (
  primaryName: string,
  aliases: string[],
  userId: string,
): Promise<CaseCounterpartyFragment[]> => {
  const userCounterparties = await caseCounterpartyOrm.getMany(
    {
      user_id: {_eq: userId},
    },
    {primary_name: Order_By.AscNullsLast},
  )

  // check for intersections between primary name and aliases
  const inputCounterparties = [
    primaryName.toLocaleUpperCase(),
    ...aliases.map((alias) => alias.toLocaleUpperCase()),
  ]

  const matchingCounterparties = userCounterparties.filter((counterparty) => {
    const counterparties = [
      counterparty.primary_name.toLocaleUpperCase(),
      ...counterparty.aliases
        .filter((alias: string) => !_.isEmpty(alias))
        .map((alias: string) => alias.toLocaleUpperCase()),
    ]
    const intersection = _.intersection(inputCounterparties, counterparties)
    return intersection.length > 0
  })
  return matchingCounterparties
}

export const isCaseSolutionItemFree = (
  solution: {offer_subscription: boolean} | null,
  item: {type: string | CaseSolutionItemType},
  tier?: BrandSubscriptionTier,
) => {
  return (
    subscriptionWithEnforcementFreeItemTypes.includes(item.type as CaseSolutionItemType) &&
    (tier === BrandSubscriptionTier.v2AdvancedEnforcement || solution?.offer_subscription)
  )
}

// returns the original array of items, but sets its price to 0 if included in the subscription
export const setFreeEligibleItems = (items: CaseSolutionItem[], tier?: BrandSubscriptionTier) =>
  items.map((item) => {
    const free = isCaseSolutionItemFree(null, item, tier)
    return {
      ...item,
      service_fee: free ? 0 : item.service_fee,
      quantity: free ? 1 : item.quantity,
      government_fee: free ? null : item.government_fee,
    }
  })

export const canUseNewCaseSolutionsDesign = (solutions: CaseSolutionForUserFragment[]) =>
  solutions.length === 1 &&
  solutions[0].items.length === 1 &&
  ((subscriptionWithEnforcementFreeItemTypes.includes(solutions[0].items[0].type) &&
    solutions[0].offer_subscription) ||
    solutions[0].offer_watchlist)
